import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TrapTypeMonitoring } from 'api/reports/types';
import useGetSTMonitoring from 'hooks/Reports/STMonitoring/MCTMonitoring';

interface InnerSTMonitoringProps {
  slug: string;
  trapType: TrapTypeMonitoring;
  startDate: string;
  endDate: string;
}

const InnerSTMonitoring: React.FC<InnerSTMonitoringProps> = ({ slug, trapType, startDate, endDate }) => {
  const { data } = useGetSTMonitoring(slug, trapType, startDate, endDate);
  const { t } = useTranslation();

  return (
    <div className="row mb-5">
      <h5 className="text-center">
        {trapType === TrapTypeMonitoring.SMST
          ? t('VisitReportsPDF.header.SMSTMonitoring')
          : t('VisitReportsPDF.header.SRSTMonitoring')}
      </h5>
      <table className="w-100">
        <thead className="">
          <tr className="ml-th-bordered-bottom-dark">
            <th
              className="text-center mct-th"
              scope="col"
              style={{ position: 'sticky', top: 0, left: 0, backgroundColor: '#fff', zIndex: 2 }}
            >
              {t('VisitReportsPDF.monitoring-table.trap-serial-number')}
            </th>
            <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.trap-number')}
            </th>
            <th className="text-center mct-th install-date" scope="col">
              {t('VisitReportsPDF.monitoring-table.install-date')}
            </th>
            <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.signal-dates')}
            </th>
            {/* <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.trigger-type')}
            </th> */}
            {/* <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.total-triggers')}
            </th> */}
            <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.signal-time')}
            </th>
            <th className="text-center mct-th" scope="col">
              {t('VisitReportsPDF.monitoring-table.mice-count')}
            </th>
          </tr>
        </thead>
        <tbody className="mct-tbody">
          {data && data.length > 0 ? (
            data.flatMap((monitoring, monitoringIndex) => {
              const snaps = monitoring.snaps ?? [];

              return snaps.flatMap((snap, snapIndex) => {
                const signals = snap.signals ?? [];

                const getModuleBase = (seq?: string) => String(seq ?? '').split('-')[0];
                const thisBase = getModuleBase(snap.sequence_number);
                const prevBase = snapIndex > 0 ? getModuleBase(snaps[snapIndex - 1]?.sequence_number) : null;

                const setBorder = prevBase && prevBase === thisBase && snapIndex !== snaps.length;

                const hasRealSignals = signals.some(
                  s => s.date && s.date !== '—' && dayjs(s.date).isValid() && (s.time?.length ?? 0) > 0,
                );

                if (!hasRealSignals) {
                  return (
                    <tr
                      key={`empty-${monitoringIndex}-${snapIndex}`}
                      className={!setBorder ? 'mct-paired-row' : undefined}
                    >
                      <td className="text-center mct-td">
                        <div className="sticky-cell">{monitoring.serial_number || 'N/A'}</div>
                      </td>
                      <td className="text-center mct-td">{snap.sequence_number || 'N/A'}</td>
                      <td className="text-center mct-td install-date">{monitoring.date_of_installation || 'N/A'}</td>
                      <td className="text-center mct-td">---</td>
                      <td className="text-center mct-td">---</td>
                      <td className="text-center mct-td">---</td>
                    </tr>
                  );
                }
                const byDate = signals.reduce(
                  (acc, s) => {
                    const dateKey = dayjs(s.date).isValid()
                      ? dayjs(s.date).format('YYYY-MM-DD')
                      : String(s.date ?? '---');
                    if (!acc[dateKey]) {
                      acc[dateKey] = {
                        date: s.date,
                        actionCount: s.action_count ?? 0,
                        times: [...(s.time ?? [])],
                      };
                    } else {
                      acc[dateKey].times.push(...(s.time ?? []));
                      acc[dateKey].actionCount += s.action_count ?? 0;
                    }
                    return acc;
                  },
                  {} as Record<string, { date: string; actionCount: number; times: any[] }>,
                );

                const dateGroups = Object.values(byDate);

                const totalRowSpan = dateGroups.reduce((sum, g) => sum + Math.max(1, g.times.length), 0);

                return dateGroups.flatMap((g, dateIndex) => {
                  const rows = g.times.length > 0 ? g.times : [null];

                  return rows.map((timeObj, timeIndex) => {
                    const dateLabel = dayjs(g.date).isValid()
                      ? dayjs(g.date).format('YYYY-MM-DD')
                      : String(g.date ?? '---');

                    const timeLabel =
                      timeObj?.inner_time && dayjs(timeObj.inner_time).isValid()
                        ? dayjs(timeObj.inner_time).format('HH:mm:ss')
                        : (timeObj?.inner_time ?? '---');

                    const miceCount = typeof timeObj?.amount === 'number' ? timeObj.amount : '---';

                    return (
                      <tr
                        key={`data-${monitoringIndex}-${snapIndex}-${dateIndex}-${timeIndex}`}
                        className={!setBorder ? 'mct-paired-row' : undefined}
                      >
                        {dateIndex === 0 && timeIndex === 0 && (
                          <>
                            <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                              <div className="sticky-cell">{monitoring.serial_number || 'N/A'}</div>
                            </td>
                            <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                              <div className="sticky-cell">{snap.sequence_number || 'N/A'}</div>
                            </td>
                            <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                              <div className="sticky-cell">{monitoring.date_of_installation || 'N/A'}</div>
                            </td>
                          </>
                        )}

                        {timeIndex === 0 && (
                          <>
                            <td className="text-center align-top mct-td" rowSpan={rows.length}>
                              {dateLabel}
                            </td>
                            {/* <td className="text-center align-top mct-td" rowSpan={rows.length}>
                              {triggerType}
                            </td> */}
                            {/* <td className="text-center align-top mct-td" rowSpan={rows.length}>
                              {g.actionCount}
                            </td> */}
                          </>
                        )}

                        <td className="text-center mct-td signal-time">{timeLabel}</td>
                        <td className="text-center mct-td signal-time">
                          <strong>{miceCount}</strong>
                        </td>
                      </tr>
                    );
                  });
                });
              });
            })
          ) : (
            <tr>
              <td colSpan={8} className="text-center mct-td">
                {t('VisitReportsPDF.header.noData')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InnerSTMonitoring;
